/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */

import { ApiResParserService } from '@api/services/api-res-parser/api-res-parser.service';
import { Options } from '@api/models/options';
import { ApiService } from '@api/services/api/api.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, map, tap, mergeMap } from 'rxjs/operators';
import {
  LabelItemDTO,
  SurveyUpdateDTO,
  ChecklistDTO,
  PositionDTO,
  WizardDTO,
  SurveyDTO,
  DomainItemDTO,
  FormDTO,
  FormUpdateDTO,
  LabelListDTO,
  UserDataDTO,
  DomainListDTO,
  WizardStepNextOutputDTO,
  ReportRequestDTO,
  AuthenticateDTO,
  IdentificationLinkDTO,
  IdentificationVerifyDTO,
  HawkScanDTO
} from '../../models';

export interface DbUserDataDTO extends UserDataDTO {
  code: string;
}

export interface DbDomainItemDTO extends DomainItemDTO {
  code: string;
}

export interface DBStructure {
  userData?: DbUserDataDTO[];
  labels?: LabelItemDTO[];
  domains?: DbDomainItemDTO[];
  positions?: PositionDTO[];
  wizards?: WizardDTO[];
  surveys?: SurveyDTO[];
  forms?: FormDTO[];
  checklists?: ChecklistDTO[];
}

@Injectable({
  providedIn: 'root'
})
export class ApiDemoService extends ApiService {
  private db: DBStructure;

  private authCode: string;

  private firstDocUpload = true;

  constructor(
    protected http: HttpClient,
    protected parser: ApiResParserService
  ) {
    super(http, parser);
  }

  getLabels(options?: Options): Observable<LabelListDTO> {
    return this.loadDB().pipe(
      mergeMap(db => this.getOutput<LabelListDTO>({ labels: db.labels }))
    );
  }

  getDomain(code: string, options?: Options): Observable<DomainListDTO> {
    const filter = (options.params as HttpParams).get('filter');

    return this.loadDB().pipe(
      mergeMap(db =>
        this.getOutput<DomainItemDTO[]>(
          db.domains.filter(
            x => x.code === code && (!filter || x.attribute === filter)
          )
        )
      ),
      map(res => ({ domainList: res }))
    );
  }

  postSecurityLogon(req: AuthenticateDTO, options?: Options): Observable<void> {
    localStorage.setItem('demoAuthCode', req['uuid-info']);

    return of('OK').pipe(
      delay(200),
      // clean res to be void as expected
      map(() => {}),
      tap(() => (this.authCode = req['uuid-info']))
    );
  }

  getSecurityVerify(options?: Options): Observable<void> {
    return of('OK').pipe(
      delay(200),
      // clean res to be void as expected
      map(() => {})
    );
  }

  getSecurityUserData(options?: Options): Observable<UserDataDTO> {
    if (!this.authCode) {
      this.authCode = localStorage.getItem('demoAuthCode');
    }

    return this.loadDB().pipe(
      mergeMap(db =>
        this.getOutput<UserDataDTO>(
          db.userData.find(x => x.code === this.authCode)
        )
      )
    );
  }

  getPosition(positionId: string, options?: Options): Observable<PositionDTO> {
    return this.loadDB().pipe(
      mergeMap(db =>
        this.getOutput<PositionDTO>(
          db.positions.find(x => x.positionId === positionId)
        )
      )
    );
  }

  getWizard(
    positionId: string,
    wizardCode: string,
    options?: Options
  ): Observable<WizardDTO> {
    return this.loadDB().pipe(
      mergeMap(db =>
        this.getOutput<WizardDTO>(
          db.wizards.find(x => x.wizardCode === wizardCode)
        )
      )
    );
  }

  putWizardStepNext(
    positionId: string,
    wizardCode: string,
    stepCode: string,
    options?: Options
  ): Observable<WizardStepNextOutputDTO> {
    this.db.wizards.forEach(wizard => {
      wizard.steps.forEach(child => {
        if (child.stepCode === stepCode) {
          child.status = 2;
        }
      });
    });

    return of({ outcome: 'OK', messages: [] }).pipe(delay(500));
  }

  getSurvey(
    positionId: string,
    surveyCode: string,
    options?: Options
  ): Observable<SurveyDTO[]> {
    return this.loadDB().pipe(
      mergeMap(db =>
        this.getOutput<SurveyDTO[]>(
          db.surveys.filter(x => x.surveyCode === surveyCode)
        )
      )
    );
  }

  putSurvey(
    positionId: string,
    surveyCode: string,
    body: SurveyUpdateDTO,
    options?: Options
  ): Observable<SurveyDTO> {
    const survey = this.db.surveys.find(x => x.surveyCode === surveyCode);

    if (survey) {
      survey.sections.forEach(section => {
        section.questions.forEach(question => {
          const update = body.questions.find(
            x => x.questionId === question.questionId
          );

          question.answers = (update && update.answers) || [];
        });
      });
    }

    return this.getOutput<SurveyDTO>(survey, 1000);
  }

  getForm(
    positionId: string,
    formCode: string,
    options?: Options
  ): Observable<FormDTO[]> {
    return this.loadDB().pipe(
      mergeMap(db =>
        this.getOutput<FormDTO[]>(db.forms.filter(x => x.page === formCode))
      )
    );
  }

  postForm(
    positionId: string,
    formCode: string,
    body: FormUpdateDTO,
    options?: Options
  ): Observable<any> {
    const form = this.db.forms.find(x => x.page === formCode);

    form.sections.forEach(section => {
      section.rows.forEach(row => {
        row.fields.forEach(field => {
          const update = body.fields.find(x => x.key === field.fieldCode);

          field.value = (update && update.value) || null;
        });
      });
    });

    return of(true).pipe(delay(1000));
  }

  getChecklist(
    positionId: string,
    options?: Options
  ): Observable<ChecklistDTO> {
    return this.loadDB().pipe(
      mergeMap(db => this.getOutput<ChecklistDTO>(db.checklists[0]))
    );
  }

  postChecklistDocumentUpload(
    positionId: string,
    docId: string,
    body: any,
    options?: Options
  ): Observable<number> {
    if (this.firstDocUpload) {
      this.firstDocUpload = false;
      return of(2).pipe(
        delay(2000),
        mergeMap(() => throwError(['La fotografia del fronte non è a fuoco']))
      );
    }

    this.setFakeUpload(docId);

    return of(2).pipe(delay(2000));
  }

  postChecklistDocumentOverride(
    positionId: string,
    docId: string,
    fileId: string,
    body: any,
    options?: Options
  ): Observable<number> {
    this.setFakeUpload(docId);

    return of(2).pipe(delay(2000));
  }

  getChecklistDocumentDownload(
    positionId: string,
    docId: string,
    options?: Options
  ): Observable<any> {
    return this.http.get(`https://www.orimi.com/pdf-test.pdf`, options);
  }

  getChecklistDocumentScan(
    positionId: string,
    docId: string,
    options?: Options
  ): Observable<HawkScanDTO> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return of<HawkScanDTO>({
      templates: [
        {
          qualityIndex: 1,
          documentPage: 'F',
          score: 0.999,
          filePage: 1,
          documentTypeCode: 'CAR',
          fields: [
            {
              field: {
                key: 'DataDiEmissione',
                value: '',
                imagePath: 'wholepage_0',
                confidence: 0.0
              }
            },
            {
              field: {
                key: 'Cognome',
                value: 'Il mio cognome',
                imagePath: 'wholepage_1',
                confidence: 0.0
              }
            },
            {
              field: {
                key: 'DataDiNascita',
                value: '',
                imagePath: 'wholepage_2',
                confidence: 0.0
              }
            },
            {
              field: {
                key: 'LuogoDiNascita',
                value: 'FOTOGRAFIA',
                imagePath: 'wholepage_3',
                confidence: 0.0
              }
            },
            {
              field: {
                key: 'Nome',
                value: '',
                imagePath: 'wholepage_4',
                confidence: 0.0
              }
            }
          ]
        }
      ],
      message: 'File processed in 5707 msecs',
      outcome: 'OK'
    }).pipe(delay(2000));
  }

  postIdentificationLink(
    method: string,
    body: {
      businessId: string;
      ndg: string;
    },
    options?: Options
  ): Observable<IdentificationLinkDTO> {
    return of({
      consumerUrl: './assets/demo/recognition.html',
      identifier: ''
    }).pipe(delay(1000));
  }

  postIdentificationVerify(
    method: string,
    body: {
      businessId: string;
      ndg: string;
    },
    options?: Options
  ): Observable<IdentificationVerifyDTO> {
    return of<IdentificationVerifyDTO>({ status: 'OK' }).pipe(delay(1000));
  }

  postReport(
    positionId: string,
    reportCode: string,
    body: ReportRequestDTO,
    options?: Options
  ): Observable<any> {
    return this.http.get(`https://www.orimi.com/pdf-test.pdf`, options);
  }

  private loadDB(): Observable<DBStructure> {
    if (!!this.db && Object.keys(this.db).length > 0) {
      return of(this.db);
    }

    return this.http.get('./assets/demo/db.json').pipe(
      map((res: DBStructure) => {
        this.db = res;

        return this.db;
      })
    );
  }

  private getOutput<T>(data: T, delayTime: number = 500): Observable<T> {
    let output = null;

    try {
      output = JSON.parse(JSON.stringify(data));
    } catch (_) {}

    return of(output).pipe(delay(delayTime));
  }

  private setFakeUpload(docId: string): void {
    this.db.checklists[0].entities.forEach(entity => {
      entity.documents.forEach(doc => {
        if (`${doc.docId}` === docId) {
          doc.lastUpload = {
            versionId: 0,
            fileId: 988,
            documentCod: doc.documentCod,
            fileName: 'file.pdf'
          };
        }
      });
    });
  }
}
